import React, { useCallback, useEffect, useRef, useState } from 'react';

import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UrlIcon from 'app/components/shared/icons/url-icon';
import { FieldInputProps, FieldProps, FormikProps } from 'formik';

import { RichTextEditorCustomMention } from './RichTextEditorCustomMention';
import {
  EditorContainer,
  EditorWrapper,
  Toolbar,
  ToolbarButton,
  LinkDialog,
  LinkInput,
  LinkButton,
} from './styled';

interface RichTextEditorProps {
  field: FieldInputProps;
  form: FormikProps<any>;
  onChange?: (html: string) => void;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  autoFocus?: boolean;
}

const LinkExtension = Link.extend({
  openOnClick: false,
}).configure({
  openOnClick: false,
  HTMLAttributes: {
    class: 'rich-text-link',
  },
});

export default function RichTextEditor({
  field,
  form,
  onChange,
  placeholder = 'Start typing...',
  onFocus,
  onBlur,
  onKeyDown,
  autoFocus = false,
}: RichTextEditorProps) {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [isEditingLink, setIsEditingLink] = useState(false);
  const linkDialogRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      LinkExtension,
      Placeholder.configure({
        placeholder: placeholder,
        includeChildren: true,
        showOnlyWhenEditable: true,
      }),
      RichTextEditorCustomMention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
      }),
    ],
    content: field.value,
    onUpdate: ({ editor }) => {
      form.setFieldValue(field.name, editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'rich-text-editor-content',
      },
    },
  });

  const handleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);

  const handleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run();
  }, [editor]);

  const handleLink = useCallback(() => {
    if (editor?.isActive('link')) {
      const currentUrl = editor.getAttributes('link').href || '';
      setLinkUrl(currentUrl);
      setIsEditingLink(true);
      setShowLinkDialog(true);
    } else {
      setLinkUrl('');
      setIsEditingLink(false);
      setShowLinkDialog(true);
    }
  }, [editor]);

  const handleAddLink = useCallback(() => {
    if (!editor) return;

    if (linkUrl) {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      if (hasSelection) {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      } else {
        editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkUrl}</a>`).run();
      }
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
    setIsEditingLink(false);
  }, [editor, linkUrl]);

  const handleCancelLink = useCallback(() => {
    setShowLinkDialog(false);
    setLinkUrl('');
    setIsEditingLink(false);
  }, []);

  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e);
    },
    [onKeyDown]
  );

  useEffect(() => {
    if (autoFocus && editor) {
      editor.commands.focus();
    }
  }, [autoFocus, editor]);

  useEffect(() => {
    if (editor && field.value !== editor.getHTML()) {
      editor.commands.setContent(field.value);
    }
  }, [field.value, editor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        linkDialogRef.current &&
        !linkDialogRef.current.contains(event.target as Node) &&
        showLinkDialog
      ) {
        handleCancelLink();
      }
    };

    if (showLinkDialog) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLinkDialog, handleCancelLink]);

  if (!editor) {
    return null;
  }

  const isBoldActive = editor.isActive('bold');
  const isUnderlineActive = editor.isActive('underline');
  const isLinkActive = editor.isActive('link');

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton
          onClick={handleBold}
          active={isBoldActive}
          title="Bold (Ctrl+B)"
          type="button"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={handleUnderline}
          active={isUnderlineActive}
          title="Underline (Ctrl+U)"
          type="button"
        >
          <u>U</u>
        </ToolbarButton>
        <div style={{ position: 'relative' }}>
          <ToolbarButton
            onClick={handleLink}
            active={isLinkActive}
            title="Link (Ctrl+K)"
            type="button"
          >
            <UrlIcon width={16} height={16} />
          </ToolbarButton>
          {showLinkDialog && (
            <LinkDialog ref={linkDialogRef}>
              <LinkInput
                type="text"
                placeholder="Enter URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddLink();
                  } else if (e.key === 'Escape') {
                    handleCancelLink();
                  }
                }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <LinkButton onClick={handleAddLink} primary>
                  {isEditingLink ? 'Update Link' : 'Add Link'}
                </LinkButton>
                {isEditingLink && (
                  <LinkButton
                    onClick={() => {
                      editor?.chain().focus().unsetLink().run();
                      handleCancelLink();
                    }}
                  >
                    Remove Link
                  </LinkButton>
                )}
                <LinkButton onClick={handleCancelLink}>Cancel</LinkButton>
              </div>
            </LinkDialog>
          )}
        </div>
      </Toolbar>
      <EditorWrapper
        editor={editor}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </EditorContainer>
  );
}
